import { Component, ElementRef, OnInit, TemplateRef, ViewChild, Input, OnDestroy, Output, EventEmitter } from '@angular/core';
import { NgbModal, NgbModalOptions, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import * as feather from 'feather-icons';
import { environment } from 'environments/environment.prod';
import { Router, NavigationEnd } from '@angular/router';
import { catchError, filter, map } from 'rxjs/operators';
import { forkJoin, of, Subscription } from 'rxjs';
import {DocumentService} from '../../../../../services/document/document.service';
import {ConversionStateService} from '../../../../../services/document/conversion-state.service';
import {getStationIdFromLocalStorage} from '../../../../../services/parametre-document/parametre-document-state.service';


export type DocumentSourceType = 'devis' | 'commande-client' | 'bon-livraison' | 'facture' | 'commande-fournisseur' | 'bon-reception' | 'facture-fournisseur';

@Component({
  selector: 'app-convert-modal',
  templateUrl: './convert-modal.component.html',
  styleUrls: ['./convert-modal.component.scss']
})
export class ConvertModalComponent implements OnInit, OnDestroy {
  @ViewChild('modalLG') modalTemplate!: TemplateRef<any>;
  @Input() sourceType?: DocumentSourceType;
  @Input() documentId?: number;
  @Output() conversionSuccess = new EventEmitter<{documentId: number, newType: string}>();

  file: string = environment.file_url;
  selectedFiles: File[] = [];
  selectedFile: File | null = null;
  private modalRef!: NgbModalRef;
  private routerSubscription?: Subscription;

  selectedDocumentType: string | null = null;
  currentUrl: string = '';
  isConverting: boolean = false;
  typeDocumentIdMap: { [key: string]: number } = {};
  typeDocuments: any[] = [];
  isLoadingTypes: boolean = false;
  /** Options déjà utilisées pour une conversion depuis ce document (ex. « Bon de réception ») */
  convertedOptionLabels: string[] = [];
  isLoadingConvertedOptions: boolean = false;

  /** station_id aligné sur le reste de l’app (listes documents) */
  private get stationId(): number {
    return getStationIdFromLocalStorage();
  }

  constructor(
    private toastr: ToastrService,
    private modalService: NgbModal,
    private router: Router,
    private documentService: DocumentService,
    private conversionStateService: ConversionStateService
  ) {}

  ngOnInit(): void {
    this.loadTypeDocuments();
    this.updateRouteOptions(this.router.url);

    this.routerSubscription = this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.updateRouteOptions(event.url);
      });
  }

  private loadTypeDocuments(): void {
    this.isLoadingTypes = true;
    this.documentService.getTypeDocuments().subscribe({
      next: (response) => {
        // Gérer différentes structures de réponse
        let typesData: any[] = [];
        if (Array.isArray(response)) {
          typesData = response;
        } else if (response?.data?.types) {
          // Structure: { success: true, data: { types: [...] } }
          typesData = Array.isArray(response.data.types) ? response.data.types : [];
        } else if (response?.data && Array.isArray(response.data)) {
          // Structure: { data: [...] }
          typesData = response.data;
        } else if (response && Array.isArray(response)) {
          typesData = response;
        }

        this.typeDocuments = typesData;
        this.typeDocumentIdMap = {};

        // Créer un mapping entre les noms affichés et les IDs (accents ignorés)
        if (Array.isArray(typesData)) {
          typesData.forEach((type: any) => {
            const t = this.normalizeTypeTitre(type.titre || type.nom || '');
            if (t.includes('COMMANDE') && !t.includes('FOURNISSEUR')) {
              this.typeDocumentIdMap['Commande'] = type.id;
            } else if (t.includes('BON DE LIVRAISON') || t.includes('BON LIVRAISON')) {
              this.typeDocumentIdMap['Bon de livraison'] = type.id;
            } else if (t.includes('FACTURE') && t.includes('FOURNISSEUR')) {
              this.typeDocumentIdMap['Facture fournisseur'] = type.id;
            } else if (t.includes('FACTURE') && !t.includes('FOURNISSEUR')) {
              this.typeDocumentIdMap['Facture'] = type.id;
            } else if (t.includes('BON') && t.includes('RECEPTION')) {
              this.typeDocumentIdMap['Bon de réception'] = type.id;
            } else if (t.includes('AVOIR') && t.includes('FOURNISSEUR')) {
              this.typeDocumentIdMap['Avoir fournisseur'] = type.id;
            } else if (t.includes('AVOIR')) {
              this.typeDocumentIdMap['Avoir'] = type.id;
            }
          });
        }

        this.isLoadingTypes = false;
        console.log('Types de documents chargés:', this.typeDocumentIdMap);
      },
      error: (error) => {
        console.error('Erreur lors du chargement des types de documents:', error);
        this.isLoadingTypes = false;
        this.typeDocuments = [];
        // Valeurs par défaut en cas d'erreur
        this.typeDocumentIdMap = {
          'Commande': 8,
          'Bon de livraison': 10,
          'Facture': 11,
          'Bon de réception': 13,
          'Facture fournisseur': 14,
          'Avoir': 15
        };
      }
    });
  }

  getTypeDocumentId(typeName: string): number | undefined {
    return this.typeDocumentIdMap[typeName];
  }

  /** Ignore accents / casse pour matcher les titres API (ex. RÉCEPTION). */
  private normalizeTypeTitre(titre: string): string {
    return String(titre || '')
      .toUpperCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  private resolveTypeIdForOption(optionLabel: string): number | undefined {
    if (optionLabel === 'Avoir' && this.isFactureFournisseurPage) {
      return this.getTypeDocumentId('Avoir fournisseur') ?? this.getTypeDocumentId('Avoir');
    }
    const mapped = this.getTypeDocumentId(optionLabel);
    if (mapped != null) {
      return mapped;
    }
    const defaults: { [key: string]: number } = {
      'Commande': 8,
      'Bon de livraison': 10,
      'Facture': 11,
      'Bon de réception': 13,
      'Facture fournisseur': 14,
      'Avoir': 15
    };
    return defaults[optionLabel];
  }

  ngOnDestroy(): void {
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
    if (this.modalRef) {
      this.modalRef.close();
    }
  }

  ngAfterViewInit(): void {
    feather.replace();
  }

  private updateRouteOptions(url: string): void {
    this.currentUrl = url;
    this.updateDefaultSelection();
  }

  // Getters pour déterminer le type de page
  get isDevisPage(): boolean {
    return this.sourceType === 'devis' || this.currentUrl.includes('/vente/devis');
  }

  get isCommandeClientPage(): boolean {
    return this.sourceType === 'commande-client' || this.currentUrl.includes('/vente/commande-client');
  }

  get isBonLivraisonPage(): boolean {
    return this.sourceType === 'bon-livraison' || this.currentUrl.includes('/vente/bon-livraison');
  }

  get isFacturePage(): boolean {
    return this.sourceType === 'facture' || this.currentUrl.includes('/vente/facture');
  }

  get isCommandeFournisseurPage(): boolean {
    return this.sourceType === 'commande-fournisseur' || this.currentUrl.includes('/achat/commande-fournisseur');
  }

  get isBonReceptionPage(): boolean {
    return this.sourceType === 'bon-reception' || this.currentUrl.includes('/achat/bon-reception');
  }

  get isFactureFournisseurPage(): boolean {
    return this.sourceType === 'facture-fournisseur' || this.currentUrl.includes('/achat/facture-fournisseur');
  }

  get hasAvailableOptions(): boolean {
    return this.isDevisPage || this.isCommandeClientPage || this.isBonLivraisonPage || 
           this.isFacturePage || this.isCommandeFournisseurPage || this.isBonReceptionPage || 
           this.isFactureFournisseurPage;
  }

  // Options disponibles selon le type de document source
  get availableOptions(): string[] {
    if (this.isDevisPage) {
      return ['Commande', 'Bon de livraison', 'Facture'];
    } else if (this.isCommandeClientPage) {
      return ['Bon de livraison', 'Facture'];
    } else if (this.isBonLivraisonPage) {
      return ['Facture'];
    } else if (this.isFacturePage) {
      return ['Avoir'];
    } else if (this.isCommandeFournisseurPage) {
      return ['Bon de réception', 'Facture fournisseur'];
    } else if (this.isBonReceptionPage) {
      return ['Facture fournisseur'];
    } else if (this.isFactureFournisseurPage) {
      return ['Avoir'];
    }
    return [];
  }

  get sourceDocumentLabel(): string {
    if (this.isDevisPage) return 'Devis';
    if (this.isCommandeClientPage) return 'Commande client';
    if (this.isBonLivraisonPage) return 'Bon de livraison';
    if (this.isFacturePage) return 'Facture';
    if (this.isCommandeFournisseurPage) return 'Commande fournisseur';
    if (this.isBonReceptionPage) return 'Bon de réception';
    if (this.isFactureFournisseurPage) return 'Facture fournisseur';
    return 'Document';
  }

  isOptionDisabled(optionLabel: string): boolean {
    return this.convertedOptionLabels.includes(optionLabel);
  }

  get hasSelectableOptions(): boolean {
    return this.availableOptions.some(opt => !this.isOptionDisabled(opt));
  }

  /** Mappe un type document API vers le libellé radio du modal */
  private resolveOptionLabelFromLinkedDoc(doc: any): string | null {
    const typeId = doc?.type_document_id ?? doc?.typeDocument?.id;
    if (typeId != null) {
      const entry = Object.entries(this.typeDocumentIdMap).find(([, id]) => id === Number(typeId));
      if (entry) {
        if (entry[0] === 'Avoir fournisseur') {
          return 'Avoir';
        }
        return entry[0];
      }
      // Fallback IDs connus si le map n'est pas encore prêt
      const byId: { [id: number]: string } = {
        8: 'Commande',
        10: 'Bon de livraison',
        11: 'Facture',
        13: 'Bon de réception',
        14: 'Facture fournisseur',
        15: 'Avoir'
      };
      if (byId[Number(typeId)]) {
        return byId[Number(typeId)];
      }
    }

    const t = this.normalizeTypeTitre(doc?.typeDocument?.titre || doc?.type_document?.titre || '');
    if (!t) {
      return null;
    }
    if (t.includes('COMMANDE') && !t.includes('FOURNISSEUR')) {
      return 'Commande';
    }
    if (t.includes('BON DE LIVRAISON') || t.includes('BON LIVRAISON')) {
      return 'Bon de livraison';
    }
    if (t.includes('BON') && t.includes('RECEPTION')) {
      return 'Bon de réception';
    }
    if (t.includes('FACTURE') && t.includes('FOURNISSEUR')) {
      return 'Facture fournisseur';
    }
    if (t.includes('FACTURE') && !t.includes('FOURNISSEUR')) {
      return 'Facture';
    }
    if (t.includes('AVOIR')) {
      return 'Avoir';
    }
    return null;
  }

  private finishConvertedOptionsLoad(labels: string[], thenOpen: () => void): void {
    this.convertedOptionLabels = labels;
    this.isLoadingConvertedOptions = false;
    this.updateDefaultSelection();
    thenOpen();
  }

  /**
   * Documents déjà créés depuis ce document (document_original_id),
   * via listes station/type (compatible API prod sans /original/:id).
   */
  private loadConvertedOptionsViaStationLists(documentId: number, thenOpen: () => void): void {
    const sid = this.stationId;
    if (!sid) {
      console.warn('[ConvertModal] stationId manquant — impossible de vérifier les conversions existantes');
      this.finishConvertedOptionsLoad([], thenOpen);
      return;
    }

    const optionQueries = this.availableOptions
      .map(opt => ({ opt, typeId: this.resolveTypeIdForOption(opt) }))
      .filter((x): x is { opt: string; typeId: number } => x.typeId != null && x.typeId > 0);

    if (!optionQueries.length) {
      console.warn('[ConvertModal] Aucun type_document_id résolu pour les options', this.availableOptions, this.typeDocumentIdMap);
      this.finishConvertedOptionsLoad([], thenOpen);
      return;
    }

    forkJoin(
      optionQueries.map(({ opt, typeId }) =>
        this.documentService.getByStationAndTypeAll(sid, typeId, 200).pipe(
          map(docs => ({ opt, typeId, docs: Array.isArray(docs) ? docs : [] })),
          catchError((err) => {
            console.error(`[ConvertModal] Échec liste station=${sid} type=${typeId}`, err);
            return of({ opt, typeId, docs: [] as any[] });
          })
        )
      )
    ).subscribe({
      next: (results) => {
        const labels: string[] = [];
        results.forEach(({ opt, docs }) => {
          const alreadyConverted = docs.some((d: any) => {
            const oid = d?.document_original_id ?? d?.documentOriginalId ?? d?.fullData?.document_original_id;
            return oid != null && Number(oid) === Number(documentId);
          });
          if (alreadyConverted) {
            labels.push(opt);
          }
        });
        console.log('[ConvertModal] Options déjà converties:', labels, 'sourceId=', documentId);
        this.finishConvertedOptionsLoad(labels, thenOpen);
      },
      error: () => this.finishConvertedOptionsLoad([], thenOpen)
    });
  }

  private loadConvertedOptions(documentId: number, thenOpen: () => void): void {
    this.isLoadingConvertedOptions = true;
    this.convertedOptionLabels = [];
    // Prod : /api/documents/original/:id peut être absent → listes station (document_original_id)
    this.loadConvertedOptionsViaStationLists(documentId, thenOpen);
  }

  private updateDefaultSelection(): void {
    const firstAvailable = this.availableOptions.find(opt => !this.isOptionDisabled(opt)) || null;
    this.selectedDocumentType = firstAvailable;
  }

  openModal(sourceType?: DocumentSourceType, documentId?: number): void {
    if (sourceType) {
      this.sourceType = sourceType;
    }
    if (documentId) {
      this.documentId = documentId;
    }

    if (!this.hasAvailableOptions) {
      this.toastr.warning('Aucune option de conversion disponible pour cette page.');
      return;
    }

    if (!this.documentId) {
      this.toastr.error('ID du document manquant pour la conversion.');
      return;
    }

    const docId = this.documentId;
    const proceed = () => {
      this.loadConvertedOptions(docId, () => {
        if (!this.hasSelectableOptions) {
          this.toastr.warning('Ce document a déjà été converti vers tous les types disponibles.');
          return;
        }

        const options: NgbModalOptions = {
          centered: true,
          backdrop: 'static',
          keyboard: true,
          size: 'lg',
          ariaLabelledBy: 'fileUploadModalLabel',
          windowClass: 'convert-modal'
        };

        this.modalRef = this.modalService.open(this.modalTemplate, options);

        setTimeout(() => {
          feather.replace();
        }, 100);
      });
    };

    // Attendre le mapping des types pour résoudre les bons type_document_id
    if (this.isLoadingTypes) {
      const waitTypes = setInterval(() => {
        if (!this.isLoadingTypes) {
          clearInterval(waitTypes);
          proceed();
        }
      }, 50);
      setTimeout(() => {
        clearInterval(waitTypes);
        proceed();
      }, 5000);
      return;
    }

    proceed();
  }

  getImageSrc(): string {
    const base = '../../../../../assets/images/logo/';
    if (!this.selectedDocumentType) {
      return base + 'Card.png';
    }

    // Devis + Facture → Card_devis_facture.png
    if (this.isDevisPage && this.selectedDocumentType === 'Facture') {
      return base + 'Card_devis_facture.png';
    }
    // Facture + Avoir → Card_facture.png
    if (this.isFacturePage && this.selectedDocumentType === 'Avoir') {
      return base + 'Card_facture.png';
    }
    // Bon de réception + Facture fournisseur → Card_bn_recep.png
    if (this.isBonReceptionPage && this.selectedDocumentType === 'Facture fournisseur') {
      return base + 'Card_bn_recep.png';
    }

    // Commande client
    if (this.isCommandeClientPage || this.sourceType === 'commande-client') {
      if (this.selectedDocumentType === 'Facture') return base + 'commande-facture.png';
      if (this.selectedDocumentType === 'Bon de livraison') return base + 'cmd-liv.png';
    }

    // Autres cas par type sélectionné
    switch (this.selectedDocumentType) {
      case 'Commande':
        return base + 'Card.png';
      case 'Bon de livraison':
        return base + 'Card1.png';
      case 'Facture':
        return base + 'Card_bn_liv.png';
      case 'Bon de réception':
        return base + 'CardAchat1.png';
      case 'Facture fournisseur':
        return base + 'CardAchat2.png';
      case 'Avoir':
        return base + 'CardAvoir.png';
      default:
        return base + 'Card.png';
    }
  }

  convertDocument(): void {
    if (!this.selectedDocumentType) {
      this.toastr.error('Veuillez sélectionner un type de document.');
      return;
    }

    if (this.isOptionDisabled(this.selectedDocumentType)) {
      this.toastr.warning(`Ce document a déjà été converti en « ${this.selectedDocumentType} ».`);
      return;
    }

    if (!this.hasAvailableOptions) {
      this.toastr.error('Aucune option de conversion disponible.');
      return;
    }

    if (!this.documentId) {
      this.toastr.error('ID du document manquant pour la conversion.');
      return;
    }
    const docId = this.documentId;

    const isAvoir = this.selectedDocumentType === 'Avoir';
    const isFacture = this.selectedDocumentType === 'Facture' || this.selectedDocumentType === 'Facture fournisseur';

    /**
     * Document cible = type « Avoir » / « Avoir fournisseur » (GET /api/type-documents), pas la facture source,
     * pour que la duplication crée un document avoir (ref_externe, compteurs, listes).
     */
    const typeDocumentId = isAvoir
      ? (this.isFactureFournisseurPage
          ? this.getTypeDocumentId('Avoir fournisseur')
          : this.getTypeDocumentId('Avoir'))
      : this.getTypeDocumentId(this.selectedDocumentType || '');
    if (!typeDocumentId) {
      this.toastr.error(`Type de document "${this.selectedDocumentType}" non reconnu.`);
      return;
    }

    this.isConverting = true;
    const sourceLabel = this.sourceDocumentLabel;

    let statutId: number;
    let payloadDocOriginalId: number | undefined;
    if (isAvoir) {
      statutId = 19; // "Non utilisé" : l'avoir créé au clic Approuver doit apparaître dans la liste Avoir fournisseur
      payloadDocOriginalId = docId; // document_original_id = facture d'origine
    } else if (isFacture) {
      statutId = 12; // Non payé
    } else {
      statutId = 9; // Crée
    }

    // Ne pas créer le document tout de suite : charger le document source et ouvrir en mode ADD.
    // Le document ne sera créé qu'au clic sur "Approuver" dans le formulaire.
    this.documentService.getDocumentById(docId).subscribe({
      next: (docRes) => {
        const data = docRes?.data ?? docRes;
        const doc = data?.document ?? data ?? docRes;
        if (!doc) {
          this.isConverting = false;
          this.toastr.error('Document source introuvable.');
          return;
        }
        // Pour facture fournisseur → avoir : le fournisseur peut être dans data.fournisseur_data (pas dans document)
        if (isAvoir && this.isFactureFournisseurPage) {
          if (data?.fournisseur_data) {
            doc.fournisseur_data = data.fournisseur_data;
          }
          if (data?.fornisseurs?.length > 0) {
            doc.fornisseurs = data.fornisseurs; // clé API typo "fornisseurs"
          }
          if (data?.fournisseurs?.length > 0) {
            doc.fournisseurs = data.fournisseurs;
          }
          if (doc.fournisseur_id == null && data?.fournisseur_id != null) {
            doc.fournisseur_id = data.fournisseur_id;
          }
          // Préférer fournisseur_id à id pour l'id du fournisseur
          if (doc.fournisseur_id == null && doc.fournisseur_data?.fournisseur_id != null) {
            doc.fournisseur_id = doc.fournisseur_data.fournisseur_id;
          }
          if (doc.fournisseur_id == null && doc.fournisseur_data?.id != null) {
            doc.fournisseur_id = doc.fournisseur_data.id;
          }
          if (doc.fournisseur_id == null && doc.fournisseurs?.[0]?.fournisseur_id != null) {
            doc.fournisseur_id = doc.fournisseurs[0].fournisseur_id;
          }
          if (doc.fournisseur_id == null && doc.fournisseurs?.[0]?.id != null) {
            doc.fournisseur_id = doc.fournisseurs[0].id;
          }
          if (doc.fournisseur_id == null && doc.fornisseurs?.[0]?.fournisseur_id != null) {
            doc.fournisseur_id = doc.fornisseurs[0].fournisseur_id;
          }
          if (doc.fournisseur_id == null && doc.fornisseurs?.[0]?.id != null) {
            doc.fournisseur_id = doc.fornisseurs[0].id;
          }
        }
        let totalsAvoir: { total_ht: number; total_tva: number; total_ttc: number; timbre_fiscal: number; net_payer: number } | undefined;
        if (isAvoir) {
          const totalHt = parseFloat(doc.total_ht) || 0;
          const totalTva = parseFloat(doc.total_tva) || 0;
          const totalTtc = parseFloat(doc.total_ttc) || 0;
          const timbre = parseFloat(doc.timbre_fiscal) || 0.6;
          const netPayerSrc = parseFloat(doc.net_payer);
          const netPayer = Number.isNaN(netPayerSrc) ? totalTtc : netPayerSrc;
          totalsAvoir = {
            total_ht: -totalHt,
            total_tva: -totalTva,
            total_ttc: -totalTtc,
            timbre_fiscal: timbre,
            net_payer: -netPayer
          };
        }
        const conversionFrom = {
          sourceId: docId,
          sourceDocument: doc,
          newType: this.selectedDocumentType,
          typeDocumentId,
          statutId,
          isAvoir,
          totalsAvoir
        };
        const basePath = this.router.url.includes('/vente') ? '/vente' : this.router.url.includes('/achat') ? '/achat' : '/vente';
        // Routes définies à la racine dans SampleModule
        const pathPrefix = '';
        let targetPath: string;
        if (isAvoir) {
          // Facture (vente) → Avoir : /vente/avoir/add-facture | Facture fournisseur → Avoir fournisseur : /achat/avoir-fournisseur/add-avoir-fournisseur
          if (this.isFactureFournisseurPage) {
            targetPath = `${pathPrefix}/achat/avoir-fournisseur/add-avoir-fournisseur`;
          } else {
            targetPath = `${pathPrefix}/vente/avoir/add-facture`;
          }
        } else {
          const normalizeType = (t: string) => (t || '').toLowerCase().trim();
          const normalizedNewType = normalizeType(this.selectedDocumentType || '');
          const typeRouteMap: { [key: string]: string } = {
            'devis': 'devis',
            'commande': 'commande-client',
            'commande client': 'commande-client',
            'bon de livraison': 'bon-livraison',
            'bon livraison': 'bon-livraison',
            'facture': 'facture',
            'bon de sortie': 'bon-sortie',
            'bon sortie': 'bon-sortie',
            'commande fournisseur': 'commande-fournisseur',
            'bon reception': 'bon-reception',
            'bon de réception': 'bon-reception',
            'facture fournisseur': 'facture-fournisseur'
          };
          let newTypeRoute = typeRouteMap[normalizedNewType];
          if (!newTypeRoute) {
            newTypeRoute = normalizedNewType.replace(/\s+/g, '-').replace(/é/g, 'e').replace(/è/g, 'e');
          }
          targetPath = `${pathPrefix}${basePath}/${newTypeRoute}/add-${newTypeRoute}`;
        }
        this.isConverting = false;
        this.conversionStateService.set(conversionFrom);
        this.modalRef?.close();
        this.toastr.success(`Préparation : ${sourceLabel} → ${this.selectedDocumentType}. Modifiez si besoin puis cliquez sur Approuver.`, 'Conversion');
        this.router.navigateByUrl(targetPath);
      },
      error: () => {
        this.isConverting = false;
        this.toastr.error('Impossible de charger le document source.', 'Erreur');
      }
    });
  }

  onDocumentTypeChange(): void {
    console.log('Type de document sélectionné:', this.selectedDocumentType);
  }
}
